import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  Input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Options, ChangeContext } from '@angular-slider/ngx-slider';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { publicMethods } from '../../../globals';
import { ZintAutocompleteComponent } from '../../../app/html-snippet-components/zint-autocomplete/zint-autocomplete.component';
import { catchError, concatMap, filter, tap } from 'rxjs/operators';
import { MessageService } from '../../../services/message.service';

interface MatcherField {
  crmField: Record<string, any>;
  id?: number | string;
}

@Component({
  selector: 'wizard-crm-company-matcher',
  templateUrl: './wizard-crm-company-matcher.component.html',
  styleUrl: './wizard-crm-company-matcher.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WizardCrmCompanyMatcherComponent {
  @ViewChildren(ZintAutocompleteComponent)
  autocompletes!: QueryList<ZintAutocompleteComponent>;

  @Input() stepData: Record<string, any>;

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  snackbar = inject(MatSnackBar);
  cdr = inject(ChangeDetectorRef);
  injector = inject(Injector);

  CRM_OBJECT_TYPE = 'crm_object_type';
  LIST_CRM_FIELDS_OPTIONS = 'list_crm_object_fields';
  MATCHER_FIELD_TYPES = 'list_matcher_field_types';
  MATCHER_FIELDS = 'matcher_fields';
  CRM_OBJECT_FIELDS = 'crm_object_fields';

  crmType: string = '';
  stepType: string = '';
  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });

  crmProperties = [];
  matcherFieldTypes = [];
  selectedMatcherFields: Record<string, MatcherField> = {};

  defaultMatcherField: MatcherField = {
    crmField: null,
  };

  minimumAcceptableMatchRate: number = 0;
  showAdvancedOptionsModal = false;
  advancedHelpModalConfig: any;
  advancedHelpModalContent: any;
  isCompanyMatcherRunning = false;

  sliderOptions: Options = {
    floor: 0,
    ceil: 100,
    showSelectionBar: true,
    animateOnMove: false,
    hideLimitLabels: true,
    hidePointerLabels: true,
  };

  ngOnInit() {
    this.crmType = this.crmService.configCRMType();
    this.stepType = this.stepData.stepType;
    this.isCompanyMatcherRunning = false;
    this.getStepActionContent();
  }

  getStepActionContent(): void {
    if (!this.crmObjectID()) {
      this.crmService
        .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
        .subscribe(data => {
          if (this.pubMethods.hasData(data)) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0]?.id);

            this.getCRMObjectAndMatcherData();
          }
        });
    } else if (this.crmObjectID()) {
      this.getCRMObjectAndMatcherData();
    }
  }

  getCRMObjectAndMatcherData(): void {
    this.crmService.getCRMObjectDetails(this.crmObjectID()).subscribe(crmObject => {
      this.minimumAcceptableMatchRate = crmObject?.minimumAcceptableMatchRate ?? 0;
      this.cdr.detectChanges();
    });

    this.getMatcherFieldTypesAndExistingMappings();
  }

  getMatcherFieldTypesAndExistingMappings(): void {
    forkJoin({
      fieldTypes: this.crmService.getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.MATCHER_FIELD_TYPES,
        objectId: this.crmObjectID(),
      }),
      crmFields: this.crmService.getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_CRM_FIELDS_OPTIONS,
        objectId: this.crmObjectID(),
        refetch: true,
      }),
      existingMappings: this.crmService.getCRMExistingState({
        actionType: this.MATCHER_FIELDS,
        objectId: this.crmObjectID(),
      }),
    })
      .pipe(
        tap(results => {
          this.matcherFieldTypes = results.fieldTypes;

          this.crmProperties = results.crmFields;

          this.processExistingMatcherFields(results.existingMappings);
        }),
        catchError(error => {
          console.error('company matcher loading error', error);
          return of([]);
        })
      )
      .subscribe();
  }

  processExistingMatcherFields(existingData: Record<string, any>[]): void {
    if (!existingData?.length) return;

    this.selectedMatcherFields = {};

    existingData.forEach(mapping => {
      this.selectedMatcherFields[mapping.fieldType] = {
        crmField: mapping.crmField,
        id: mapping.id,
      };
    });

    this.cdr.markForCheck();
  }

  getFieldTypeLabel(fieldTypeValue: string): string {
    if (!fieldTypeValue || fieldTypeValue === 'undefined' || fieldTypeValue === 'null') {
      return '';
    }

    if (!this.matcherFieldTypes?.length) {
      return fieldTypeValue;
    }
    const type = this.matcherFieldTypes.find((type: any) => type.value === fieldTypeValue);
    return type ? type.label : fieldTypeValue;
  }

  onMatcherFieldSelected(fieldTypeValue: string, selectedCrmField: Record<string, any>): void {
    const existingField = this.selectedMatcherFields[fieldTypeValue];

    if (existingField?.id) {
      this.updateMatcherField(fieldTypeValue, selectedCrmField);
    } else {
      this.createMatcherField(fieldTypeValue, selectedCrmField);
    }
  }

  onMatcherFieldCleared(fieldTypeValue: string): void {
    const existingField = this.selectedMatcherFields[fieldTypeValue];

    if (existingField?.id) {
      this.removeMatcherField(fieldTypeValue);
    }
  }

  fetchCRMFields(): void {
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_CRM_FIELDS_OPTIONS,
        objectId: this.crmObjectID(),
        refetch: true,
      })
      .subscribe(data => {
        this.crmProperties = data;
      });
  }

  createMatcherField(fieldTypeValue: string, selectedCrmField: Record<string, any>): void {
    const reqBodyForCrmField = { ...selectedCrmField, lookupField: null };
    let fieldIdToPostMatcherField = null;

    this.crmService
      .postCRMStepActionJSON({
        postActionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        params: reqBodyForCrmField,
      })
      .pipe(
        tap(response => {
          if (response.ok) {
            fieldIdToPostMatcherField = response.body.id;
            this.selectedMatcherFields[fieldTypeValue] = {
              crmField: selectedCrmField,
              id: response.body.id,
            };
          } else {
            const errorDetails = response.error?.error || response.body;
            if (errorDetails.nonFieldErrors) {
              const errMsg = errorDetails.nonFieldErrors;
              this.messageService.show(errMsg);
            } else {
              console.warn('something went wrong', response);
            }
          }
        }),
        filter(response => response.ok),
        concatMap(() => {
          const reqBodyForMatcherField = {
            crm_field_id: fieldIdToPostMatcherField,
            field_type: fieldTypeValue,
          };

          return this.crmService.postCRMStepActionJSON({
            postActionType: this.MATCHER_FIELDS,
            objectId: this.crmObjectID(),
            params: reqBodyForMatcherField,
          });
        }),
        tap(response => {
          if (response.ok) {
            this.selectedMatcherFields[fieldTypeValue].id = response.body.id;
            this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }

  updateMatcherField(fieldTypeValue: string, selectedCrmField: Record<string, any>): void {
    const existingField = this.selectedMatcherFields[fieldTypeValue];

    if (existingField.crmField?.name === selectedCrmField.name) {
      return;
    }

    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.MATCHER_FIELDS,
        objectId: this.crmObjectID(),
        fieldId: Number(existingField.id),
        updateMethod: 'delete',
      })
      .pipe(
        filter(response => response.ok),
        concatMap(() => {
          const reqBodyForCrmField = { ...selectedCrmField, lookupField: null };
          return this.crmService.postCRMStepActionJSON({
            postActionType: this.CRM_OBJECT_FIELDS,
            objectId: this.crmObjectID(),
            params: reqBodyForCrmField,
          });
        }),
        tap(response => {
          if (response.ok) {
            const newFieldId = response.body.id;
            this.selectedMatcherFields[fieldTypeValue] = {
              crmField: selectedCrmField,
              id: newFieldId,
            };

            const reqBodyForMatcherField = {
              crm_field_id: newFieldId,
              field_type: fieldTypeValue,
            };

            return this.crmService.postCRMStepActionJSON({
              postActionType: this.MATCHER_FIELDS,
              objectId: this.crmObjectID(),
              params: reqBodyForMatcherField,
            });
          }
        }),
        tap(response => {
          if (response.ok) {
            this.selectedMatcherFields[fieldTypeValue].id = response.body.id;
            this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }

  removeMatcherField(fieldTypeValue: string): void {
    const mappingToDelete = this.selectedMatcherFields[fieldTypeValue];

    if (!mappingToDelete) return;

    const { id } = mappingToDelete;

    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.MATCHER_FIELDS,
        objectId: this.crmObjectID(),
        fieldId: Number(id),
        updateMethod: 'delete',
      })
      .subscribe(response => {
        if (response.ok) {
          delete this.selectedMatcherFields[fieldTypeValue];
          this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
          this.cdr.markForCheck();
        }
      });
  }

  onMinimumMatchRateChange(changeContext: ChangeContext): void {
    this.minimumAcceptableMatchRate = changeContext.value;
    this.updateMinimumAcceptableMatchRate();
  }

  updateMinimumAcceptableMatchRate(): void {
    const updateData = {
      minimumAcceptableMatchRate: this.minimumAcceptableMatchRate
    };

    this.crmService.updateCRMObject(this.crmObjectID(), updateData).subscribe({
      next: (response) => {
        if (response.ok) {
          this.snackbar.open('Match rate updated successfully', 'OK', {
            duration: 2200,
          });
        } else {
          console.warn('Failed to update match rate', response);
          this.snackbar.open('Failed to update match rate', 'X', {
            duration: 2200,
          });
        }
      },
      error: (error) => {
        console.error('Error updating match rate', error);
        this.snackbar.open('Error updating match rate', 'X', {
          duration: 2200,
        });
      }
    });
  }

  toggleAdvancedOptionModal(): void {
    this.advancedHelpModalContent = {
      htmlElement: `
        <p>The minimum acceptable match rate determines the threshold for company matching confidence.</p>
        <p>Higher values (closer to 100) will be more strict, requiring higher confidence matches.</p>
        <p>Lower values (closer to 0) will be more lenient, accepting lower confidence matches.</p>
        <p>The default value of 65 provides a good balance between accuracy and coverage.</p>
      ` as any as HTMLElement,
    };
    this.advancedHelpModalConfig = { vCentered: true, name: 'matchRateHelp' };
    this.showAdvancedOptionsModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.advancedHelpModalConfig.name);
  }

  triggerCompanyMatcher(): void {
    if (this.isCompanyMatcherRunning) {
      return;
    }

    this.isCompanyMatcherRunning = true;
    this.crmService.triggerCompanyMatcher(this.crmObjectID()).subscribe({
      next: (response: any) => {
        this.snackbar.open('Company matcher process triggered successfully', 'OK', {
          duration: 3000,
        });
      },
      error: (error) => {
        console.error('Error triggering company matcher:', error);
        this.snackbar.open('Failed to trigger company matcher process', 'X', {
          duration: 3000,
        });
        this.isCompanyMatcherRunning = false;
        this.cdr.markForCheck();
      }
    });
  }
}
